import React from 'react';
import { useFormikContext } from 'formik';
import { IoMdWarning } from 'react-icons/io';
import dayjs from 'dayjs';

import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';

interface Props {
  seriesId: string;
  initialSeriesStartDate: string;
  initialSeriesEndDate: string;
  initialEventStartDate: string;
  toggleConfirmModal: () => void;
  isOpen: boolean;
}

const ConfirmModal = ({
  seriesId,
  initialEventStartDate,
  initialSeriesStartDate,
  initialSeriesEndDate,
  toggleConfirmModal,
  isOpen
}: Props) => {
  const { values, submitForm } = useFormikContext<any>();

  const hasSeriesStartDateChanged =
    (!initialSeriesStartDate &&
      !dayjs(initialEventStartDate).isSame(values.startDate)) ||
    (initialSeriesStartDate &&
      !dayjs(initialSeriesStartDate).isSame(values.startDate));

  let modalTitle = 'Create Recurring Drafts?';
  if (seriesId) {
    modalTitle = 'Updating Recurring Settings';
  } else if (hasSeriesStartDateChanged) {
    modalTitle = 'Update First Event and Create Series';
  }

  let modalContent =
    'This will create the selected number of linked drafts, each with the same details and settings as your first event. These drafts can be individually edited after creation.';

  if (seriesId) {
    if (!values.repeats) {
      modalContent =
        'Setting this event to "Does not repeat" will keep the first event but delete all draft instances in the series, including those with custom edits. Are you sure you want to continue?';
    } else if (values.endsAfter !== initialSeriesEndDate) {
      modalContent =
        'Changing the “ends after” date will remove all events beyond the new end date. This includes any events with custom edits. Are you sure you want to proceed?';
    } else {
      modalContent =
        'These changes will overwrite any custom edits made to individual events in this recurring series.';
    }
  }

  return (
    <Modal
      visible={isOpen}
      onClose={toggleConfirmModal}
      type={ModalType.SMALL}
      autoHeight
      title={modalTitle}
      icon={
        <div className="bg-yellow-100 flex-1 h-full flex items-center justify-center text-yellow-800">
          <IoMdWarning size={20} />
        </div>
      }
      renderFooter={() => (
        <div className="flex gap-5">
          <Button
            type={ButtonType.Inverted}
            onClick={toggleConfirmModal}
            newRadius
          >
            Cancel
          </Button>
          <Button type={ButtonType.Primary} onClick={submitForm} newRadius>
            {seriesId ? 'Confirm Changes' : 'Ok, Continue'}
          </Button>
        </div>
      )}
    >
      <div className="p-4">
        {hasSeriesStartDateChanged && (
          <p className="text-base font-normal text-neutral-600 pb-5">
            Updating the start date / time will adjust the current start date /
            time of your first draft.
          </p>
        )}
        <p className="text-base font-normal text-neutral-600">{modalContent}</p>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
